import { clsx } from 'clsx';
import { Trash2, Shield, User, Users } from 'lucide-react';
import { NetworkSegment, NetworkAccessLevel } from '../../types/configurator';
import { Button } from '../ui';
import { ValidatedInput } from '../ui/ValidatedInput';
import { validateCIDR, validateIPv4 } from '../../utils/validation';

interface NetworkSegmentCardProps {
  segment: NetworkSegment;
  onUpdate: (updates: Partial<NetworkSegment>) => void;
  onRemove: () => void;
}

const ACCESS_ICONS: Record<NetworkAccessLevel, typeof Shield> = {
  provider: Shield,
  customer: User,
  shared: Users,
};

const ACCESS_LABELS: Record<NetworkAccessLevel, string> = {
  provider: 'Provider',
  customer: 'Customer',
  shared: 'Shared',
};

const ACCESS_COLORS: Record<NetworkAccessLevel, { bg: string; text: string }> = {
  provider: { bg: 'bg-orange-100', text: 'text-orange-700' },
  customer: { bg: 'bg-blue-100', text: 'text-blue-700' },
  shared: { bg: 'bg-purple-100', text: 'text-purple-700' },
};

export function NetworkSegmentCard({ segment, onUpdate, onRemove }: NetworkSegmentCardProps) {
  const AccessIcon = ACCESS_ICONS[segment.accessLevel];
  const accessColors = ACCESS_COLORS[segment.accessLevel];

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-4"
      style={{ borderLeftColor: segment.color, borderLeftWidth: 4 }}
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="h-4 w-4 rounded-full"
            style={{ backgroundColor: segment.color }}
          />
          <input
            type="text"
            value={segment.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            className="border-none bg-transparent font-medium text-gray-900 focus:outline-none focus:ring-0"
          />
          <span
            className={clsx(
              'flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium',
              accessColors.bg,
              accessColors.text
            )}
          >
            <AccessIcon className="h-3 w-3" />
            {ACCESS_LABELS[segment.accessLevel]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={segment.enabled}
              onChange={(e) => onUpdate({ enabled: e.target.checked })}
              className="rounded border-gray-300 text-hermit-600 focus:ring-hermit-500"
            />
            <span className="text-gray-600">Enabled</span>
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-gray-400 hover:text-red-500"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Configuration */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">Subnet</label>
          <ValidatedInput
            value={segment.subnet}
            onChange={(value) => onUpdate({ subnet: value })}
            validate={validateCIDR}
            placeholder="192.168.200.0/24"
            className="text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">Gateway</label>
          <ValidatedInput
            value={segment.gateway}
            onChange={(value) => onUpdate({ gateway: value })}
            validate={validateIPv4}
            placeholder="192.168.200.1"
            className="text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">VLAN ID</label>
          <input
            type="number"
            value={segment.vlanId || ''}
            onChange={(e) => onUpdate({ vlanId: parseInt(e.target.value, 10) || undefined })}
            placeholder="200"
            min={1}
            max={4094}
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-hermit-500 focus:outline-none focus:ring-1 focus:ring-hermit-500"
          />
        </div>
      </div>

      {/* Access level selector */}
      <div className="mt-3">
        <label className="mb-1 block text-xs font-medium text-gray-500">Access Level</label>
        <div className="flex gap-2">
          {(['provider', 'customer', 'shared'] as NetworkAccessLevel[]).map((level) => {
            const Icon = ACCESS_ICONS[level];
            const colors = ACCESS_COLORS[level];
            const isSelected = segment.accessLevel === level;

            return (
              <button
                key={level}
                onClick={() => onUpdate({ accessLevel: level })}
                className={clsx(
                  'flex items-center gap-1 rounded px-3 py-1.5 text-xs font-medium transition-colors',
                  isSelected
                    ? `${colors.bg} ${colors.text} ring-2 ring-offset-1 ring-current`
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                <Icon className="h-3 w-3" />
                {ACCESS_LABELS[level]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Description */}
      <p className="mt-3 text-xs text-gray-500">{segment.description}</p>
    </div>
  );
}
